/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import {
    TRACKING_INFORMATION_ACTION_REVEAL,
    TRACKING_INFORMATION_UI_TYPE_FAQ,
    TRACKING_LOGGED_IN,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_TARGET_AUDIENCE_CONSUMER,
    TRACKING_TYPE_ERROR,
    TRACKING_TYPE_INFORMATION,
    TRACKING_TYPE_PAGEVIEW,
    TRACKING_TYPE_SWITCH_OPTIONS,
    trackIt,
} from '@vfde-sails/tracking';
import { debugInfo } from '../util/debug';
import {
    TrackingBasePayload,
    TrackingContact,
    TrackingError,
    TrackingForm,
    TrackingInformation,
    TrackingPageview,
    TrackingSelectedOption,
} from './interface';
import {
    LMFormTrackingState,
    TrackingEventName,
    TrackingFormStepName,
    TrackingTrigger,
    TrackingUIType,
} from './enum';
import { toTrackingString } from '../util/formatter';
import {
    getTrackingOptions,
    getHotlineNumbers,
    getSwitcherActionKey,
    getTrackingOrderObject,
    getTrackingProduct,
    getFormStepByName,
    getFormStatusByName,
    toTrackingItem,
    getFormFieldsByAvailability,
    getErrorType,
    getTrackingFootprint,
    pageLevels,
} from './util';
import {
    JarvisProduct,
    JarvisTvProduct,
} from '../interface';
import {
    TRACKING_AVC_FORM_NAME,
    TRACKING_BUSINESS_TRANSACTION_TYPE,
    TRACKING_FLOW_TYPE,
    TRACKING_TYPE_AVC,
    TRACKING_SITE_AREA,
    TRACKING_PAGE_LEVEL_AVC,
    TRACKING_ERROR_ACTION,
    TRACKING_TYPE_CONTACT,
    TRACKING_INTEREST_FIB,
    TRACKING_ACTION_ENGAGE,
    TRACKING_ACTION_REVEAL,
} from './constants';
import { getFootnoteLinkText } from '../components/footnoteLink';
import { AppState } from '../container/App/interface';
import { ApiErrorResponseWithCode } from '@vfde-sails/utils';
import { ErrorType } from '../container/App/enum';

/**
 * Track accordion opening
 *
 * @param headlineText - The content of the accordion headline that triggered the opening.
 */
export const trackFaqAccordion = (headlineText: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: toTrackingString(headlineText),
            information_ui_type: TRACKING_INFORMATION_UI_TYPE_FAQ,
            information_trigger: TrackingEventName.ACCORDION,
        };
        trackIt(TRACKING_TYPE_INFORMATION, payload);
        debugInfo(`Track ${TrackingEventName.ACCORDION}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.ACCORDION} error:`, e);
    }
};

/**
 * Track overlay opening
 *
 * @param overlayName - The overlay's name to be opened
 */
export const trackOverlayOpening = (overlayName: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: toTrackingString(overlayName),
            information_ui_type: TrackingUIType.OVERLAY,
            information_trigger: TrackingTrigger.BUTTON,
        };
        trackIt(TRACKING_TYPE_INFORMATION, payload);
        debugInfo(`Track ${TrackingEventName.OVERLAY}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.OVERLAY} error:`, e);
    }
};

/**
 * Track footnote link click
 *
 * @param text - The content of the footnote link that triggered the opening.
 */
export const trackFootnoteLink = (text: string) => {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: toTrackingString(text),
            information_ui_type: TrackingUIType.FOOTNOTE,
            information_trigger: TrackingTrigger.REFERENCE_FOOTNOTE,
        };
        trackIt(TRACKING_TYPE_INFORMATION, payload);
        debugInfo(`Track ${TrackingEventName.FOOTNOTE_LINK}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.FOOTNOTE_LINK} error:`, e);
    }
};

/**
 * Event for tooltip icon hover tracking
 */
export function trackTooltipEvent (this: HTMLElement) {
    try {
        const payload: TrackingInformation = {
            information_action: TRACKING_INFORMATION_ACTION_REVEAL,
            information_name: toTrackingString(getFootnoteLinkText(this, 2)),
            information_ui_type: TrackingUIType.TOOLTIP,
            information_trigger: TrackingTrigger.ICON,
        };
        trackIt(TRACKING_TYPE_INFORMATION, payload);
        debugInfo(`Track ${TrackingEventName.TOOLTIP}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.TOOLTIP} error:`, e);
    }
}

/**
 * Tracks the status of the Gigakombi switcher
 */
export const trackGigakombiSwitcher = (
    { options, gigakombiDiscount }: JarvisProduct,
    isSwitcherOn: boolean,
) => {
    try {
        const payload: TrackingSelectedOption = {
            [getSwitcherActionKey(isSwitcherOn)]: getTrackingOptions(options, gigakombiDiscount),
        };
        trackIt(TRACKING_TYPE_SWITCH_OPTIONS, payload);
        debugInfo(`Track ${TrackingEventName.OPTION_PICKER}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.OPTION_PICKER} error:`, e);
    }
};

/**
 * Tracks the status of the TV Connect switcher
 */
export const trackTvSwitcher = (tv: JarvisTvProduct, isSwitcherOn: boolean) => {
    try {
        const payload: TrackingSelectedOption = {
            [getSwitcherActionKey(isSwitcherOn)]: [
                getTrackingProduct(tv),
            ],
        };
        trackIt(TRACKING_TYPE_SWITCH_OPTIONS, payload);
        debugInfo(`Track ${TrackingEventName.OPTION_PICKER}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TrackingEventName.OPTION_PICKER} error:`, e);
    }
};

const getTrackingBase = (state: AppState) => {
    const { isLoggedIn, customer, customerType } = state;

    const payload: TrackingBasePayload = {
        login_status: isLoggedIn ? TRACKING_LOGGED_IN : TRACKING_NOT_LOGGED_IN,
        page_type: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        site_area: TRACKING_SITE_AREA,
        platform_type: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        business_transaction_type: TRACKING_BUSINESS_TRANSACTION_TYPE,
        line_of_customer: TRACKING_TARGET_AUDIENCE_CONSUMER,
        flow_type: TRACKING_FLOW_TYPE,
        page_levels: pageLevels,
        hotline_numbers: getHotlineNumbers(),
    };

    if (isLoggedIn && customer && customerType) {
        payload.existing_customer_footprint = getTrackingFootprint(customerType);

        if (customer.existingBandwidth) {
            payload.existing_customer_bandwidth = `${customer.existingBandwidth} mbit`;
        }
    }

    return payload;
};

/**
 * Fires the general Pageview tracking event
 */
export const trackPageview = (state: AppState) => {
    try {
        const payload: TrackingPageview = {
            ...getTrackingBase(state),
            oOrder: getTrackingOrderObject(state),
        };
        trackIt(TRACKING_TYPE_PAGEVIEW, payload);
        debugInfo(`Track ${TRACKING_TYPE_PAGEVIEW}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TRACKING_TYPE_PAGEVIEW} error:`, e);
    }
};

/**
 * Tracks the AVC form steps
 *
 * @param state - Current state
 * @param stepName - Name of the step
 */
export const trackAvcForm = (state: AppState, stepName: TrackingFormStepName) => {
    try {
        const pageviewBase = getTrackingBase(state);
        const payload: TrackingForm = {
            ...pageviewBase,
            page_type: TRACKING_TYPE_AVC,
            page_levels: [
                ...pageviewBase.page_levels,
                TRACKING_PAGE_LEVEL_AVC,
                stepName,
            ],
            form_name: TRACKING_AVC_FORM_NAME,
            form_status: getFormStatusByName(stepName),
            form_step: getFormStepByName(stepName),
            form_step_name: stepName,
            form_type: TRACKING_TYPE_AVC,
            ...getFormFieldsByAvailability(state, stepName),
        };

        if (state.preSelectedTariff) {
            payload.selected_product = toTrackingItem(state.preSelectedTariff);
        }

        trackIt(TRACKING_TYPE_PAGEVIEW, payload);
        debugInfo(`Track ${TRACKING_TYPE_PAGEVIEW}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TRACKING_TYPE_PAGEVIEW} error:`, e);
    }
};

/**
 * Tracks the captcha
 */
export const trackCaptcha = () => {
    trackIt(TrackingTrigger.CAPTCHA, {});
    debugInfo(`Track ${TrackingEventName.CAPTCHA}:`, {});
};

/**
 * Tracks if error occurs
 *
 * @param error - Error object
 * @param type - Type of the rrror
 */
export const trackError = (
    error: Error | ApiErrorResponseWithCode,
    type: ErrorType,
) => {
    try {
        const payload: TrackingError = {
            error_action: TRACKING_ERROR_ACTION,
            error_type: getErrorType(type),
            error_message: toTrackingString(error.message),
            ...(error instanceof ApiErrorResponseWithCode) ? {
                error_code: error.status.toString(),
            } : {},
        };
        trackIt(TRACKING_TYPE_ERROR, payload);
        debugInfo(`Track ${TRACKING_TYPE_ERROR}:`, payload);
    }
    catch (e) {
        debugInfo(`Track ${TRACKING_TYPE_ERROR} error:`, e);
    }
};

/**
 * Tracks the LM form.
 *
 * @param state - State of the LM form
 */
export const trackLMForm = (state: LMFormTrackingState) => {
    const payload: TrackingContact = {
        contact_subject: TRACKING_INTEREST_FIB,
        contact_tool: TrackingUIType.LM_FORM,
        contact_action: state === LMFormTrackingState.OPEN
            ? TRACKING_ACTION_REVEAL
            : TRACKING_ACTION_ENGAGE,
        contact_trigger: state === LMFormTrackingState.OPEN
            ? TrackingTrigger.BUTTON
            : TrackingTrigger.AUTOMATICALLY,
    };
    trackIt(TRACKING_TYPE_CONTACT, payload);
    debugInfo(`Track ${TrackingEventName.LM_FORM}:`, payload);
};
